import type { DataTableColumn } from "@etudescarre/ui";
import { isNil } from "@etudescarre/utils";
import { FilePdf as FilePdfIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import type { paths } from "@/lib/api/schema";
import { formatDay } from "@/lib/utils/date";
import { Classeur, type FilterDef } from "../components/classeur";
import { useClasseurFilters } from "../components/classeur/use-classeur-data";
import { openBordereauPdf } from "../components/document-table/open-pieces";

// Écrans 007, 009, 031, 037 et 038 : les bordereaux validés, émis par mon
// contrat ou reçus par lui. Le PDF se génère à la demande depuis le bordereau.

type SlipsRoute = paths["/api/yards/{yardId}/slips"]["get"];
type Slip =
  SlipsRoute["responses"][200]["content"]["application/json"]["data"]["items"][number];
type SlipType = SlipsRoute["parameters"]["query"]["type"];
type SlipRow = Omit<Slip, "id"> & { id: string; slipId: number };

type ClasseurBordereauxProps = {
  title: string;
  type: SlipType;
  emptyMessage: string;
  /** Emitted classeurs only: a recipient has no use for this filter (N164). */
  filterByRecipient?: boolean;
};

function sortedOptions(values: string[]) {
  return [...new Set(values)]
    .sort((a, b) => a.localeCompare(b, "fr", { numeric: true }))
    .map((value) => ({ value, label: value }));
}

/** By contract id: the URL joins filter values with commas, a name may hold one. */
function recipientOptions(items: SlipRow[]) {
  const names = new Map(
    items
      .flatMap((item) => item.recipients)
      .map(({ contractId, name }) => [String(contractId), name]),
  );
  return [...names]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, "fr"));
}

function notifyLoadFailure() {
  toaster.error({
    title: "Bordereaux non chargés",
    description: "La liste des bordereaux n'a pas pu être chargée.",
  });
}

function matches(selected: string[] | undefined, values: string[]) {
  return (
    isNil(selected) ||
    selected.length === 0 ||
    values.some((value) => selected.includes(value))
  );
}

function ClasseurBordereaux({
  title,
  type,
  emptyMessage,
  filterByRecipient = false,
}: ClasseurBordereauxProps) {
  const { yardId = "" } = useParams<{ yardId: string }>();
  const [items, setItems] = useState<SlipRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setIsLoading(true);
      try {
        const { data } = await apiClient.GET("/api/yards/{yardId}/slips", {
          params: { path: { yardId }, query: { type } },
        });
        if (cancelled) return;
        if (isNil(data)) notifyLoadFailure();
        setItems(
          (data?.data.items ?? []).map((item) => ({
            ...item,
            id: String(item.id),
            slipId: item.id,
          })),
        );
      } catch (error) {
        console.error("Failed to load bordereaux", error);
        if (!cancelled) notifyLoadFailure();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [yardId, type]);

  // Options come from the full list so they stay stable while filtering.
  const filters = useMemo<FilterDef[]>(
    () => [
      ...(filterByRecipient
        ? [
            {
              key: "destinataire",
              label: "Destinataire",
              options: recipientOptions(items),
            },
          ]
        : []),
      {
        key: "document",
        label: "N° de document",
        options: sortedOptions(
          items.flatMap((item) => item.documents.map((d) => d.number)),
        ),
      },
      {
        key: "lot",
        label: "Lot",
        options: sortedOptions(
          items.flatMap((item) => item.documents.flatMap((d) => d.lots)),
        ),
      },
    ],
    [items, filterByRecipient],
  );

  const { activeFilters, onFilterChange } = useClasseurFilters({ filters });

  const visibleItems = useMemo(
    () =>
      items.filter(
        (item) =>
          matches(
            activeFilters.destinataire,
            item.recipients.map((r) => String(r.contractId)),
          ) &&
          matches(
            activeFilters.document,
            item.documents.map((d) => d.number),
          ) &&
          matches(
            activeFilters.lot,
            item.documents.flatMap((d) => d.lots),
          ),
      ),
    [items, activeFilters],
  );

  const columns = useMemo<DataTableColumn<SlipRow>[]>(
    () => [
      {
        id: "number",
        header: "N°",
        accessor: "number",
        cell: ({ row }) => row.number ?? "-",
      },
      {
        id: "createdAt",
        header: "Date création",
        accessor: "createdAt",
        cell: ({ row }) => formatDay(row.createdAt),
      },
      {
        id: "recipientCount",
        header: "Destinataires",
        accessor: "recipientCount",
      },
      { id: "documentCount", header: "Documents", accessor: "documentCount" },
      {
        id: "pdf",
        header: "PDF",
        accessor: "slipId",
        cell: ({ row }) => (
          <button
            type="button"
            aria-label={`Ouvrir le bordereau ${row.number ?? ""}`}
            title="Ouvrir le PDF du bordereau"
            className="text-red-600 hover:text-red-700"
            onClick={() =>
              openBordereauPdf(
                yardId,
                row.slipId,
                row.exemplaireTeamId ?? undefined,
              )
            }
          >
            <FilePdfIcon size={18} />
          </button>
        ),
      },
    ],
    [yardId],
  );

  return (
    <Classeur
      title={title}
      items={visibleItems}
      columns={columns}
      filters={filters}
      activeFilters={activeFilters}
      onFilterChange={onFilterChange}
      isLoading={isLoading}
      emptyMessage={emptyMessage}
      total={visibleItems.length}
    />
  );
}

export function ClasseurBordereauxEmisDO() {
  return (
    <ClasseurBordereaux
      title="Classeur bordereaux émis pour avis"
      type="emis_do"
      emptyMessage="Aucun bordereau émis"
      filterByRecipient
    />
  );
}

export function ClasseurBordereauxEmisPresta() {
  return (
    <ClasseurBordereaux
      title="Classeur bordereaux émis coordination"
      type="emis_presta"
      emptyMessage="Aucun bordereau émis"
      filterByRecipient
    />
  );
}

export function ClasseurBordereauxRecus() {
  return (
    <ClasseurBordereaux
      title="Classeur bordereaux reçus"
      type="recus_presta"
      emptyMessage="Aucun bordereau reçu"
    />
  );
}

export function ClasseurBordereauxRemis() {
  return (
    <ClasseurBordereaux
      title="Classeur bordereaux remis"
      type="emis_eg"
      emptyMessage="Aucun bordereau remis"
    />
  );
}
